import { useLocale } from "../i18n/useLocale.js";

function formatNumber(value, localeTag, digits = 0) {
  return new Intl.NumberFormat(localeTag, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(value);
}

function shouldShowThicknessBreakdown(breakdown, newSheetCount, restSheetCount) {
  if (breakdown.length === 0) return false;
  if (breakdown.length > 1) return true;
  if (restSheetCount > 0) return true;

  const group = breakdown[0];
  const groupNew = group.newSheetCount ?? group.sheetCount;
  const groupRest = group.restSheetCount || 0;
  return groupNew !== newSheetCount || groupRest !== restSheetCount;
}

export function LiveResultCard({ layout }) {
  const { display } = useLocale();
  const {
    newSheetCount = layout.metrics.sheetCount,
    restSheetCount = 0,
    utilisation,
    partCount,
    usedArea,
  } = layout.metrics;
  const utilisationValue = Math.min(utilisation, 100);
  const showThickness = shouldShowThicknessBreakdown(
    layout.breakdown,
    newSheetCount,
    restSheetCount,
  );

  return (
    <section className="rail-card summary-section" aria-labelledby="result-card-title">
      <h2 id="result-card-title" className="summary-card-title">
        {display.t("web.sections.result")}
      </h2>

      <div
        className={`result-material-grid${restSheetCount > 0 ? "" : " result-material-grid--single"}`}
        role="group"
        aria-label={display.t("web.result.sheetsGroupLabel")}
      >
        <div className="result-material-stat result-material-stat--new">
          <strong>{newSheetCount}</strong>
          <span>
            {display.t(newSheetCount === 1 ? "web.result.newSheet" : "web.result.newSheets")}
          </span>
        </div>
        {restSheetCount > 0 && (
          <div className="result-material-stat result-material-stat--rest">
            <strong>{restSheetCount}</strong>
            <span>
              {display.t(restSheetCount === 1 ? "web.result.restPiece" : "web.result.restPieces")}
            </span>
          </div>
        )}
      </div>

      <div className="result-utilisation">
        <div
          className="meter"
          role="progressbar"
          aria-label={display.t("web.result.utilisationLabel")}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={utilisationValue}
          aria-valuetext={display.t("web.result.utilisationPercent", {
            value: formatNumber(utilisation, display.localeTag, 1),
          })}
        >
          <span style={{ width: `${utilisationValue}%` }} />
        </div>
        <strong>
          {display.t("web.result.utilisationValue", {
            value: formatNumber(utilisation, display.localeTag, 1),
          })}
        </strong>
      </div>
      <p className="result-utilisation-hint">{display.t("web.result.utilisationHint")}</p>

      <div className="result-meta">
        <span><strong>{partCount}</strong> {display.t("web.result.partsSuffix")}</span>
        <span><strong>{display.formatArea(usedArea)}</strong> {display.t("web.result.areaSuffix")}</span>
      </div>

      {showThickness && (
        <div className="result-thickness-chips" aria-label={display.t("web.result.thicknessLabel")}>
          {layout.breakdown.map((group) => {
            const groupNew = group.newSheetCount ?? group.sheetCount;
            const groupRest = group.restSheetCount || 0;
            const parts = [display.t("web.result.thicknessNew", { count: groupNew })];
            if (groupRest > 0) {
              parts.push(display.t("web.result.thicknessRest", { count: groupRest }));
            }

            return (
              <div className="result-thickness-chip" key={group.thickness}>
                <b>{display.formatThickness(group.thickness)}</b>
                <span>{parts.join(", ")}</span>
              </div>
            );
          })}
        </div>
      )}

      {layout.invalid.length > 0 && (
        <div className="warning">
          <strong>{display.t("web.result.invalid", { count: layout.invalid.length })}</strong>
          {layout.invalid.map((item) => <div key={item.id}>{item.projectName} · {item.name}</div>)}
        </div>
      )}
    </section>
  );
}
